import React from 'react';
import { motion } from 'framer-motion';
import { ShuffleIcon, FileTextIcon, UsersIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
const EASE = [0.23, 1, 0.32, 1] as const;
type Segment = {
  text: string;
  bold?: boolean;
};
type Stat = {
  icon: typeof ShuffleIcon;
  value: {
    fr: string;
    en: string;
  };
  body: {
    fr: Segment[];
    en: Segment[];
  };
  source: {
    fr: string;
    en: string;
  };
};
const STATS: Stat[] = [{
  icon: ShuffleIcon,
  value: {
    fr: '65 %',
    en: '65%'
  },
  body: {
    fr: [{
      text: 'des Canadiens ont dû changer de canal '
    }, {
      text: 'au moins une fois',
      bold: true
    }, {
      text: ' pour accéder à un '
    }, {
      text: 'service gouvernemental.',
      bold: true
    }],
    en: [{
      text: 'of Canadians had to switch channels '
    }, {
      text: 'at least once',
      bold: true
    }, {
      text: ' to access a '
    }, {
      text: 'government service.',
      bold: true
    }]
  },
  source: {
    fr: 'McKinsey — Canada, 2023',
    en: 'McKinsey — Canada, 2023'
  }
}, {
  icon: FileTextIcon,
  value: {
    fr: '65,8 %',
    en: '65.8%'
  },
  body: {
    fr: [{
      text: 'des Canadiens utilisant des services '
    }, {
      text: 'gouvernementaux numériques ont rencontré au moins un problème',
      bold: true
    }, {
      text: ' en 2022.'
    }],
    en: [{
      text: 'of Canadians using '
    }, {
      text: 'digital government services encountered at least one problem',
      bold: true
    }, {
      text: ' in 2022.'
    }]
  },
  source: {
    fr: 'Statistique Canada, 2026',
    en: 'Statistics Canada, 2026'
  }
}, {
  icon: UsersIcon,
  value: {
    fr: '62 %',
    en: '62%'
  },
  body: {
    fr: [{
      text: 'des employés du secteur public indiquent que des processus inefficaces nuisent à leur capacité '
    }, {
      text: 'd’offrir',
      bold: true
    }, {
      text: ' les services essentiels.'
    }],
    en: [{
      text: 'of government workers say inefficient processes affect their ability '
    }, {
      text: 'to deliver',
      bold: true
    }, {
      text: ' essential services.'
    }]
  },
  source: {
    fr: 'Accenture, 2025',
    en: 'Accenture, 2025'
  }
}];
export function ProblemEvidence() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const t = (fr: string, en: string) => isFR ? fr : en;
  return <motion.div initial={{
    opacity: 0,
    y: 14
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.3,
    ease: EASE
  }} className="mt-8 rounded-2xl border border-[#E6E4F7] bg-[#F8F7FE] px-6 md:px-9 pt-8 pb-6" data-id="element-5944" data-mp="22uks35ahdhoh">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-8 lg:gap-0" data-id="element-5945" data-mp="2hqih83lzllr7">
        <div className="lg:pr-8" data-id="element-5946" data-mp="nbps8nflzt88">
          <p className="font-sans text-[12px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5]" data-id="element-5947" data-mp="ncrbvgaoxo1k">
            {t('Ce n’est pas un problème isolé', 'This isn’t an isolated problem')}
          </p>
          <h3 className="font-serif text-[2rem] md:text-[2.3rem] text-[#121212] leading-[1.08] mt-3" data-id="element-5948" data-mp="jutu1y4mcknc">
            {t('Des faits qui', 'Facts that')}
            <span className="block" data-id="element-5949" data-mp="f18yecqi02rb">{t('parlent d’eux-mêmes.', 'speak for themselves.')}</span>
          </h3>
        </div>

        {STATS.map(({
        icon: Icon,
        value,
        body,
        source
      }, i) => <div key={source.en} className={`flex gap-4 lg:px-7 ${i > 0 ? 'lg:border-l lg:border-[#DEDCF1]' : ''} ${i > 0 ? 'border-t border-[#E6E4F7] pt-8 lg:border-t-0 lg:pt-0' : ''}`} data-id="element-5950" data-mp="60asfowi1l63" data-mpi={i}>
            <span className="w-12 h-12 shrink-0 rounded-full bg-[#ECEBFC] flex items-center justify-center" aria-hidden="true" data-id="element-5951" data-mp="tqwxd7b7ipbv">
              <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} data-id="element-5952" data-mp="oslniwkwwetn" />
            </span>
            <div className="min-w-0" data-id="element-5953" data-mp="3ejrzcu0zb2xn">
              <p className="font-sans text-[2.3rem] font-semibold text-[#4F46E5] leading-none tracking-tight whitespace-nowrap" data-id="element-5954" data-mp="39j40xnaf7jte">
                {isFR ? value.fr : value.en}
              </p>
              <p className="font-sans text-[13px] text-[#5B6478] leading-[1.55] mt-3" data-id="element-5955" data-mp="118l1d7z5c4hy">
                {(isFR ? body.fr : body.en).map((s, k) => s.bold ? <strong key={k} className="font-semibold text-[#2A2F3C]" data-id="element-5956" data-mp="sbkhnen0uuku">
                      {s.text}
                    </strong> : <React.Fragment key={k} data-id="element-5957" data-mp="1k1yquxk3biq6">{s.text}</React.Fragment>)}
              </p>
              <p className="font-sans text-[11px] text-[#8A90A0] mt-4" data-id="element-5958" data-mp="1ozgqfxc7wlli">{isFR ? source.fr : source.en}</p>
            </div>
          </div>)}
      </div>

      <div className="mt-7 pt-5 border-t border-[#E6E4F7] flex flex-col md:flex-row md:items-center gap-4 md:gap-7" data-id="element-5959" data-mp="1fysmjvgjg1mc">
        <span className="flex items-center gap-2 shrink-0 md:pr-7 md:border-r md:border-[#DEDCF1]" aria-label={t('OCDE', 'OECD')} data-id="element-5960" data-mp="3vpeu0hdsh53w">
          <span className="w-6 h-6 rounded-full bg-[#2F4A7A] flex items-center justify-center" aria-hidden="true" data-id="element-5961" data-mp="4lghjh91lcyl">
            <span className="w-3 h-3 rounded-full border-2 border-white/70" data-id="element-5962" data-mp="3sadborz7qyg8" />
          </span>
          <span className="font-sans text-[20px] tracking-tight text-[#4A5468]" aria-hidden="true" data-id="element-5963" data-mp="3o9052foaqr89">
            {t('OCDE', 'OECD')}
          </span>
        </span>
        <p className="flex-1 font-sans text-[13.5px] text-[#5B6478] leading-relaxed" data-id="element-5964" data-mp="13iyjvr8i3is">
          {t('L’OCDE désigne la fragmentation des points d’entrée, la répétition de l’information et les transferts déconnectés entre services comme des sources persistantes de complexité dans les services publics.', 'The OECD identifies fragmented entry points, repeated information and disconnected handoffs between services as persistent sources of complexity in public services.')}
        </p>
        <p className="font-sans text-[11px] text-[#8A90A0] md:self-end shrink-0" data-id="element-5965" data-mp="2qpp151v861o1">
          {t('Digital Government Outlook 2026 — OCDE', 'Digital Government Outlook 2026 — OECD')}
        </p>
      </div>
    </motion.div>;
}